import { useState, useEffect, lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import FloatingPanel from './FloatingPanel';

// Heavy pages are lazy-loaded so they don't bloat the initial bundle.
const _FloatingChat = lazy(() => import('../pages/staff/Chat'));
const _FloatingCalendar = lazy(() => import('../pages/staff/Calendar'));

/**
 * FloatingDock
 *
 * Mounted ONCE at the App level (outside <Routes>) so the Chat & Calendar
 * floating bubbles persist across page navigation.  Previously these lived
 * inside <DashboardHeader>, which unmounts/remounts on every route change —
 * causing the minimised chat bubble to vanish whenever the user navigated.
 *
 * Open/close is driven entirely by the global `bbc:floating-toggle` event
 * dispatched from the header trigger buttons (which remain in the layout).
 */
export default function FloatingDock() {
  const { token } = useAuth();
  const location = useLocation();
  const [calendarPanelOpen, setCalendarPanelOpen] = useState(false);
  const [chatPanelOpen, setChatPanelOpen] = useState(false);

  const { unreadTotal = 0 } = useChat();

  // Single source of truth for the bubbles — toggled via global events so
  // any trigger (desktop/mobile header, customer or staff) can drive them.
  useEffect(() => {
    const onToggle = (e) => {
      const which = e.detail?.which;
      if (which === 'calendar') {
        setChatPanelOpen(false);
        setCalendarPanelOpen((v) => !v);
      } else if (which === 'chat') {
        setCalendarPanelOpen(false);
        setChatPanelOpen((v) => !v);
      } else if (which === 'close') {
        setCalendarPanelOpen(false);
        setChatPanelOpen(false);
      }
    };
    window.addEventListener('bbc:floating-toggle', onToggle);
    return () => window.removeEventListener('bbc:floating-toggle', onToggle);
  }, []);

  const path = location.pathname;
  const isAppRoute = (
    path.startsWith('/staff') ||
    path.startsWith('/customer') ||
    path.startsWith('/investor')
  );
  const isCustomerOrInvestor = path.startsWith('/customer') || path.startsWith('/investor');

  // Only show the dock inside the authenticated app shell.
  if (!token || !isAppRoute) return null;

  return (
    <>
      {/* Calendar bubble — staff portals only */}
      {!isCustomerOrInvestor && (
        <FloatingPanel
          open={calendarPanelOpen}
          onClose={() => setCalendarPanelOpen(false)}
          title="Calendar"
          testId="floating-calendar-panel"
          defaultWidth={640}
          defaultHeight={720}
        >
          <Suspense fallback={<div className="p-6 text-sm text-muted-foreground">Loading calendar…</div>}>
            <_FloatingCalendar floating />
          </Suspense>
        </FloatingPanel>
      )}
      <FloatingPanel
        open={chatPanelOpen}
        onClose={() => setChatPanelOpen(false)}
        title="Chat & Calls"
        testId="floating-chat-panel"
        defaultWidth={720}
        defaultHeight={720}
        icon={<MessageSquare className="w-5 h-5" />}
        badge={unreadTotal}
      >
        <Suspense fallback={<div className="p-6 text-sm text-muted-foreground">Loading chat…</div>}>
          <_FloatingChat floating />
        </Suspense>
      </FloatingPanel>
    </>
  );
}
